import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { toast } from '@/components/ui/toaster'
import { api } from '@/lib/api'
import { getProductScreenshotThumbnailUrl } from '@/lib/assets'
import { useAuthStore } from '@/stores/auth'
import type {
  Asset,
  ForeplayAd,
  GenerationResult,
  Product,
  ScoredAd,
  Task,
} from '@/types'
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Link as LinkIcon,
  Loader2,
  Plus,
  RotateCcw,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  AdCountSelector,
  CollapsibleAdSection,
  SelectionModeToggle,
  deduplicateAllAds,
} from '@/components/reference-ads'
import type { SelectionMode } from '@/components/reference-ads'
import { ResultDetailModal } from '@/components/ResultDetailModal'
import {
  OnboardingActionBar,
  OnboardingContentFrame,
  OnboardingHeader,
  OnboardingStepProgress,
  type OnboardingStepItem,
} from '@/components/onboarding'

const ONBOARDING_MAX_SELECTION = 5
const ONBOARDING_DEFAULT_COUNT = 3

const STEPS: OnboardingStepItem[] = [
  { id: 1, name: 'Product', description: 'Enter URL & confirm details' },
  { id: 2, name: 'References', description: 'Search and select references' },
  { id: 3, name: 'Generate', description: 'Review and start generation' },
  { id: 4, name: 'Results', description: 'Review and complete onboarding' },
]

interface SearchResults {
  product_related: ForeplayAd[]
  competitor_brands: ForeplayAd[]
}

interface EditableProduct {
  product_name: string
  description: string
  selling_points: string
  target_market: string
  target_audience: string
  price: string
  language: string
  keywords: string[]
  competitor_brands: string[]
}

const EMPTY_EDITABLE_PRODUCT: EditableProduct = {
  product_name: '',
  description: '',
  selling_points: '',
  target_market: '',
  target_audience: '',
  price: '',
  language: 'en',
  keywords: [],
  competitor_brands: [],
}

function toEditableProduct(product: Product): EditableProduct {
  return {
    product_name: product.product_name || '',
    description: product.description || '',
    selling_points: product.selling_points || '',
    target_market: product.target_market || '',
    target_audience: product.target_audience || '',
    price: product.price || '',
    language: product.language || 'en',
    keywords: product.keywords || [],
    competitor_brands: product.competitor_brands || [],
  }
}

function normalizeTag(value: string): string {
  return value.trim().toLowerCase()
}

function hasTag(tags: string[], value: string): boolean {
  const normalized = normalizeTag(value)
  return tags.some((tag) => normalizeTag(tag) === normalized)
}

function TagEditor({
  label,
  helper,
  tags,
  inputValue,
  onInputChange,
  onAdd,
  onRemove,
  placeholder,
  variant = 'primary',
}: {
  label: string
  helper: string
  tags: string[]
  inputValue: string
  onInputChange: (value: string) => void
  onAdd: (value: string) => void
  onRemove: (idx: number) => void
  placeholder: string
  variant?: 'primary' | 'competitor'
}) {
  const tagClass =
    variant === 'primary'
      ? 'bg-spectrum-blue/10 text-spectrum-blue'
      : 'bg-spectrum-orange/10 text-spectrum-orange'

  const tagHoverClass =
    variant === 'primary' ? 'hover:bg-spectrum-blue/20' : 'hover:bg-spectrum-orange/20'

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <p className="text-xs text-gray-500">{helper}</p>

      <div className="flex min-h-10 flex-wrap gap-2 rounded-xl border border-gray-200 bg-gray-50/80 p-2">
        {tags.length === 0 && <span className="self-center text-xs text-gray-400">No tags yet</span>}
        {tags.map((tag, idx) => (
          <span
            key={`${tag}-${idx}`}
            className={cn('inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium', tagClass)}
          >
            {tag}
            <button
              type="button"
              onClick={() => onRemove(idx)}
              className={cn('rounded-full p-0.5 transition-colors', tagHoverClass)}
              aria-label={`Remove ${tag}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>

      <div className="flex gap-2">
        <Input
          value={inputValue}
          placeholder={placeholder}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onAdd(inputValue)
            }
          }}
        />
        <Button type="button" variant="outline" size="icon" onClick={() => onAdd(inputValue)}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

function ImagePreviewModal({
  imageUrl,
  altText,
  onClose,
}: {
  imageUrl: string
  altText: string
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div className="relative max-h-[90vh] max-w-4xl">
        <button
          onClick={onClose}
          className="absolute -right-2 -top-12 text-white transition-colors hover:text-gray-300"
          aria-label="Close preview"
        >
          <X className="h-8 w-8" />
        </button>
        <img
          src={imageUrl}
          alt={altText}
          className="max-h-[85vh] max-w-full rounded-lg object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  )
}

function StepLoadingStatus({
  isSearching,
  isScoring,
  scoringDone,
}: {
  isSearching: boolean
  isScoring: boolean
  scoringDone: boolean
}) {
  return (
    <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4">
      <div className="space-y-2">
        <div className="h-3 w-40 animate-pulse rounded bg-gray-200" />
        <div className="h-3 w-72 animate-pulse rounded bg-gray-100" />
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-gray-700">
          {isSearching ? (
            <Loader2 className="h-4 w-4 animate-spin text-spectrum-blue" />
          ) : (
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          )}
          Searching reference ads
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-700">
          {isScoring ? (
            <Loader2 className="h-4 w-4 animate-spin text-spectrum-blue" />
          ) : scoringDone ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          ) : (
            <div className="h-4 w-4 rounded-full border border-gray-300" />
          )}
          Scoring relevance and quality
        </div>
      </div>
    </div>
  )
}

export function OnboardingPage() {
  const navigate = useNavigate()
  const { user, setUser, subscription, fetchSubscription, isAuthenticated } = useAuthStore()

  const [currentStep, setCurrentStep] = useState(1)
  const [productUrl, setProductUrl] = useState(() => localStorage.getItem('pending_product_url') || '')
  const [product, setProduct] = useState<Product | null>(null)
  const [keywords, setKeywords] = useState<string[]>([])
  const [competitorBrands, setCompetitorBrands] = useState<string[]>([])
  const [generatedDescription, setGeneratedDescription] = useState('')
  const [selectedAds, setSelectedAds] = useState<string[]>([])
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null)
  const [scoredAds, setScoredAds] = useState<ScoredAd[]>([])
  const [generatedAssets, setGeneratedAssets] = useState<Asset[]>([])
  const [generationTaskId, setGenerationTaskId] = useState<string | null>(null)

  const [newKeyword, setNewKeyword] = useState('')
  const [newBrand, setNewBrand] = useState('')
  const [productParsed, setProductParsed] = useState(false)
  const [showProductNameError, setShowProductNameError] = useState(false)

  const [expandProductRelated, setExpandProductRelated] = useState(true)
  const [expandCompetitorBrands, setExpandCompetitorBrands] = useState(true)

  const [selectionMode, setSelectionMode] = useState<SelectionMode>('auto')
  const [autoSelectCount, setAutoSelectCount] = useState(ONBOARDING_DEFAULT_COUNT)

  const [previewAd, setPreviewAd] = useState<ForeplayAd | null>(null)
  const [editableProduct, setEditableProduct] = useState<EditableProduct>(EMPTY_EDITABLE_PRODUCT)
  const [selectedResultIdx, setSelectedResultIdx] = useState<number | null>(null)

  const urlInputRef = useRef<HTMLInputElement | null>(null)
  const productNameInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (isAuthenticated && !subscription) {
      fetchSubscription()
    }
  }, [isAuthenticated, subscription, fetchSubscription])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [currentStep])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (currentStep !== 1) return
      if (!productParsed) {
        urlInputRef.current?.focus()
        return
      }
      productNameInputRef.current?.focus()
    }, 160)

    return () => window.clearTimeout(timer)
  }, [currentStep, productParsed])

  useEffect(() => {
    if (!previewAd) return

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPreviewAd(null)
      }
    }

    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [previewAd])

  const scoredAdsMap = useMemo(() => {
    const map = new Map<string, number>()
    scoredAds.forEach((scoredAd) => {
      map.set(scoredAd.ad.id, scoredAd.overall_score)
    })
    return map
  }, [scoredAds])

  const referenceAdsMap = useMemo(() => {
    const map: Record<string, ForeplayAd> = {}
    scoredAds.forEach((scoredAd) => {
      map[scoredAd.ad.id] = scoredAd.ad
    })

    if (searchResults) {
      ;[...searchResults.product_related, ...searchResults.competitor_brands].forEach((ad) => {
        if (!map[ad.id]) {
          map[ad.id] = ad
        }
      })
    }

    return map
  }, [scoredAds, searchResults])

  const creditsRequired = Math.max(selectedAds.length, 1)
  const creditsAvailable = subscription?.credits_available || 0
  const hasEnoughCredits = creditsAvailable >= creditsRequired

  const handleSkipOnboarding = async () => {
    try {
      await api.completeOnboarding({})
      if (user) {
        setUser({ ...user, is_onboarded: true })
      }
      navigate('/home')
    } catch (error: any) {
      toast({
        title: 'Failed to skip onboarding',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      })
    }
  }

  const { data: taskData } = useQuery<Task>({
    queryKey: ['task', generationTaskId],
    queryFn: () => api.getTask(generationTaskId!),
    enabled: !!generationTaskId,
    refetchInterval: (query) => {
      const task = query.state.data
      if (task?.status === 'completed' || task?.status === 'failed') {
        return false
      }
      return 5000
    },
    staleTime: 4000,
  })

  const { data: taskResults } = useQuery<Asset[]>({
    queryKey: ['task-results', generationTaskId],
    queryFn: () => api.getTaskResults(generationTaskId!),
    enabled: !!generationTaskId && taskData?.status === 'completed',
  })

  useEffect(() => {
    if (taskResults && taskResults.length > 0) {
      setGeneratedAssets(taskResults)
    }
  }, [taskResults])

  useEffect(() => {
    if (taskData?.status === 'completed' || taskData?.status === 'failed') {
      fetchSubscription()
    }
  }, [taskData?.status, fetchSubscription])

  useEffect(() => {
    if (taskData?.status === 'failed') {
      toast({
        title: 'Generation failed',
        description: taskData.error_message || 'Failed to generate creatives',
        variant: 'destructive',
      })
    }
  }, [taskData?.status, taskData?.error_message])

  const scoreMutation = useMutation({
    mutationFn: async (params: { ad_ids: string[]; product_id: string; top_n?: number }) => {
      return api.scoreReferenceAds(params)
    },
    onSuccess: (data) => {
      setScoredAds(data.scored_ads)
      const topN = data.scored_ads.slice(0, autoSelectCount).map((s) => s.ad.id)
      setSelectedAds(topN)
      toast({
        title: 'Scoring complete',
        description: `Top ${autoSelectCount} references selected automatically`,
      })
    },
    onError: (error: any) => {
      toast({
        title: 'Scoring failed',
        description: error.response?.data?.detail || 'Could not score ads',
        variant: 'destructive',
      })
    },
  })

  const searchAdsMutation = useMutation({
    mutationFn: async (params?: {
      description?: string
      keywords?: string[]
      competitor_brands?: string[]
    }) => {
      return api.searchReferenceAdsV2({
        description: params?.description ?? generatedDescription,
        cate: '',
        keywords: params?.keywords ?? keywords,
        competitor_brands: params?.competitor_brands ?? competitorBrands,
        media_type: 'image',
        limit: 20,
      })
    },
    onSuccess: (data) => {
      const { productAds, competitorAds } = deduplicateAllAds(
        data.product_related,
        data.competitor_brands
      )

      setSearchResults({
        product_related: productAds,
        competitor_brands: competitorAds,
      })

      const allAdIds = [...productAds.map((ad) => ad.id), ...competitorAds.map((ad) => ad.id)]

      if (allAdIds.length > 0 && product) {
        scoreMutation.mutate({
          ad_ids: allAdIds,
          product_id: product.id,
          top_n: allAdIds.length,
        })
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Search failed',
        description: error.response?.data?.detail || 'Failed to search reference ads',
        variant: 'destructive',
      })
    },
  })

  const createProductMutation = useMutation({
    mutationFn: async (url: string) => {
      localStorage.removeItem('pending_product_url')
      return api.createProductFromUrl(url)
    },
    onSuccess: (data) => {
      toast({ title: 'Product parsed', description: 'Information extracted successfully' })
      setProduct(data)
      setEditableProduct(toEditableProduct(data))
      setKeywords(data.keywords || [])
      setCompetitorBrands(data.competitor_brands || [])
      setGeneratedDescription(data.description || '')
      setProductParsed(true)
      setShowProductNameError(false)
    },
    onError: (error: any) => {
      if (error.response?.status === 401) return
      toast({
        title: 'Failed to parse URL',
        description: error.response?.data?.error?.message || 'Please check URL and try again',
        variant: 'destructive',
      })
    },
  })

  const updateProductMutation = useMutation({
    mutationFn: async (productData: EditableProduct) => {
      if (!product) throw new Error('Missing product id')
      return api.updateProduct(product.id, productData)
    },
    onSuccess: (data) => {
      toast({ title: 'Product saved', description: 'Product information updated' })
      setProduct(data)
      setKeywords(data.keywords || [])
      setCompetitorBrands(data.competitor_brands || [])
      setGeneratedDescription(data.description || '')

      searchAdsMutation.mutate({
        description: data.description || '',
        keywords: data.keywords || [],
        competitor_brands: data.competitor_brands || [],
      })
      setCurrentStep(2)
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to save product',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      })
    },
  })

  const generateMutation = useMutation({
    mutationFn: async () => {
      if (!product) throw new Error('Missing product id')
      return api.generateCompetitorResearch({
        product_id: product.id,
        reference_ad_ids: selectedAds,
        keywords,
        model: 'gemini_image',
        count: creditsRequired,
        aspect_ratio: '1:1',
      })
    },
    onSuccess: (data) => {
      toast({ title: 'Generation started', description: 'Creating your creatives...' })
      setGenerationTaskId(data.id)
      setCurrentStep(4)
    },
    onError: (error: any) => {
      if (error.response?.status === 402) {
        toast({
          title: 'Insufficient credits',
          description: 'Please upgrade your plan to continue',
          variant: 'destructive',
        })
        return
      }

      const message = error.response?.data?.detail || error.message || 'Failed to generate'
      toast({
        title: 'Generation failed',
        description: message,
        variant: 'destructive',
      })
    },
  })

  const completeOnboarding = useMutation({
    mutationFn: async () => api.completeOnboarding({}),
    onSuccess: () => {
      if (user) {
        setUser({ ...user, is_onboarded: true })
      }
      navigate('/home')
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to complete onboarding',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      })
    },
  })

  const handleChangeUrl = () => {
    setProductParsed(false)
    setProductUrl('')
    setProduct(null)
    setSearchResults(null)
    setScoredAds([])
    setSelectedAds([])
  }

  const handleSelectAd = (adId: string) => {
    if (selectedAds.includes(adId)) {
      setSelectedAds((prev) => prev.filter((id) => id !== adId))
      return
    }

    if (selectedAds.length >= ONBOARDING_MAX_SELECTION) {
      toast({ title: `Maximum ${ONBOARDING_MAX_SELECTION} references allowed`, variant: 'destructive' })
      return
    }

    setSelectedAds((prev) => [...prev, adId])
  }

  const selectTopN = (count: number) => {
    if (scoredAds.length === 0) return
    const topN = scoredAds.slice(0, count).map((s) => s.ad.id)
    setSelectedAds(topN)
  }

  const handleSelectionModeChange = (mode: SelectionMode) => {
    setSelectionMode(mode)
    if (mode === 'auto') {
      selectTopN(autoSelectCount)
    }
  }

  const handleAutoSelectCountChange = (count: number) => {
    setAutoSelectCount(count)
    if (selectionMode === 'auto') {
      selectTopN(count)
    }
  }

  const addKeywordTag = (value: string) => {
    const trimmed = value.trim()
    if (!trimmed) return

    if (hasTag(editableProduct.keywords, trimmed)) {
      toast({ title: 'Keyword already exists', description: trimmed })
      return
    }

    setEditableProduct((prev) => ({
      ...prev,
      keywords: [...prev.keywords, trimmed],
    }))
    setNewKeyword('')
  }

  const addBrandTag = (value: string) => {
    const trimmed = value.trim()
    if (!trimmed) return

    if (hasTag(editableProduct.competitor_brands, trimmed)) {
      toast({ title: 'Competitor brand already exists', description: trimmed })
      return
    }

    setEditableProduct((prev) => ({
      ...prev,
      competitor_brands: [...prev.competitor_brands, trimmed],
    }))
    setNewBrand('')
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1)
    }
  }

  const handleNext = () => {
    if (currentStep === 1) {
      if (!productParsed) {
        if (!productUrl.trim()) {
          toast({ title: 'Please enter a product URL', variant: 'destructive' })
          return
        }
        createProductMutation.mutate(productUrl)
        return
      }

      if (!editableProduct.product_name.trim()) {
        setShowProductNameError(true)
        toast({ title: 'Product name is required', variant: 'destructive' })
        return
      }

      setShowProductNameError(false)
      updateProductMutation.mutate(editableProduct)
      return
    }

    if (currentStep === 2) {
      if (selectedAds.length === 0) return
      setCurrentStep(3)
      return
    }

    if (currentStep === 3) {
      if (!hasEnoughCredits) return
      generateMutation.mutate()
      return
    }

    if (currentStep === 4) {
      completeOnboarding.mutate()
    }
  }

  const stepMeta = useMemo(() => {
    if (currentStep === 1) {
      if (!productParsed) {
        return {
          title: 'Start with your product URL',
          description: 'We will extract key product details and prepare references automatically.',
        }
      }

      return {
        title: 'Confirm product details',
        description: 'Review the extracted info so ad search and generation are more accurate.',
      }
    }

    if (currentStep === 2) {
      return {
        title: 'Choose your references',
        description: 'Auto-pick top references or fine-tune manually for better creative direction.',
      }
    }

    if (currentStep === 3) {
      return {
        title: 'Review before generation',
        description: 'Check output and credit usage before launching your first generation.',
      }
    }

    return {
      title: 'Generation results',
      description:
        'Review generated creatives and finish onboarding. Usually around 1 minute - your first winning batch is on the way.',
    }
  }, [currentStep, productParsed])

  const stepLabel = `Step ${currentStep} of ${STEPS.length}`

  const nextButtonLabel =
    currentStep === 1 && productParsed
      ? 'Confirm & Continue'
      : currentStep === 1 && !productParsed
      ? 'Parse URL'
      : currentStep === 3
      ? 'Generate Creatives'
      : currentStep === 4
      ? 'Finish Onboarding'
      : 'Continue'

  const nextButtonDisabled =
    (currentStep === 1 && !productParsed && !productUrl.trim()) ||
    (currentStep === 1 && productParsed && !editableProduct.product_name.trim()) ||
    (currentStep === 2 && selectedAds.length === 0) ||
    (currentStep === 3 && !hasEnoughCredits) ||
    (currentStep === 4 && taskData?.status !== 'completed') ||
    createProductMutation.isPending ||
    updateProductMutation.isPending ||
    searchAdsMutation.isPending ||
    scoreMutation.isPending ||
    generateMutation.isPending ||
    completeOnboarding.isPending

  const isActionLoading =
    createProductMutation.isPending ||
    updateProductMutation.isPending ||
    generateMutation.isPending ||
    completeOnboarding.isPending

  const loadingActionLabel =
    createProductMutation.isPending
      ? 'Parsing URL...'
      : updateProductMutation.isPending
      ? 'Saving product...'
      : generateMutation.isPending
      ? 'Starting generation...'
      : completeOnboarding.isPending
      ? 'Finishing...'
      : 'Processing...'

  const creditsHint =
    currentStep === 3 ? `This generation will consume ${creditsRequired} credit(s).` : undefined

  const taskProgressRatio = (() => {
    if (!taskData) return 0
    if (taskData.status === 'completed') return 100
    if (taskData.status === 'failed') return 100
    if (!taskData.requested_count) return 10
    return Math.min(95, Math.round((taskData.success_count / taskData.requested_count) * 100))
  })()

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[-12%] top-[-8%] h-[28rem] w-[28rem] rounded-full bg-spectrum-blue/10 blur-3xl" />
        <div className="absolute right-[-14%] top-[12%] h-[26rem] w-[26rem] rounded-full bg-spectrum-teal/10 blur-3xl" />
        <div className="absolute bottom-[-18%] left-[20%] h-[26rem] w-[26rem] rounded-full bg-spectrum-cyan/10 blur-3xl" />
      </div>

      {(createProductMutation.isPending || updateProductMutation.isPending) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-sm rounded-2xl border border-white/20 bg-white p-8 text-center shadow-2xl">
            <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-spectrum-blue" />
            <p className="text-lg font-semibold text-gray-900">
              {createProductMutation.isPending ? 'Parsing product information...' : 'Saving product information...'}
            </p>
            <p className="mt-2 text-sm text-gray-500">
              {createProductMutation.isPending
                ? 'This usually takes 30-60 seconds.'
                : 'Applying your updates now...'}
            </p>
          </div>
        </div>
      )}

      <div className="relative mx-auto flex w-full max-w-6xl flex-col gap-5 px-4 pb-28 pt-6 sm:px-6 sm:pb-32 sm:pt-8">
        <OnboardingHeader creditsAvailable={creditsAvailable} />

        <OnboardingStepProgress steps={STEPS} currentStep={currentStep} />

        <OnboardingContentFrame title={stepMeta.title} description={stepMeta.description} stepLabel={stepLabel}>
          {currentStep === 1 && (
            <div className="space-y-6">
              {!productParsed && (
                <div className="grid gap-4 rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-5 sm:p-6 lg:grid-cols-[1fr_260px]">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Enter your product URL</h3>
                      <p className="mt-1 text-sm text-gray-600">
                        We will extract product info, keywords, and competitor brands for onboarding.
                      </p>
                    </div>

                    <div className="rounded-2xl border border-gray-200 bg-white p-2 shadow-sm">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <div className="relative flex-1">
                          <LinkIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                          <Input
                            ref={urlInputRef}
                            type="url"
                            placeholder="https://your-store.com/products/..."
                            value={productUrl}
                            onChange={(e) => setProductUrl(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                handleNext()
                              }
                            }}
                            className="h-12 border-0 pl-10 text-sm shadow-none focus-visible:ring-0"
                          />
                        </div>
                        <Button
                          type="button"
                          className="h-12 bg-gradient-to-r from-spectrum-blue to-spectrum-cyan text-white"
                          onClick={handleNext}
                          disabled={!productUrl.trim() || createProductMutation.isPending}
                        >
                          {createProductMutation.isPending ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Parsing...
                            </>
                          ) : (
                            'Parse URL'
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-spectrum-blue/25 bg-spectrum-blue/5 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-spectrum-blue">What happens next</p>
                    <ul className="mt-3 space-y-2 text-sm text-gray-700">
                      <li className="flex gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-spectrum-blue" />
                        Extract product profile and screenshot
                      </li>
                      <li className="flex gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-spectrum-blue" />
                        Generate keywords and competitor signals
                      </li>
                      <li className="flex gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-spectrum-blue" />
                        Find and score high-quality references
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              {productParsed && (
                <div className="space-y-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Confirm product information</h3>
                      <p className="text-sm text-gray-600">
                        Edit details before searching references and generating creatives.
                      </p>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={handleChangeUrl}>
                      <RotateCcw className="mr-1 h-4 w-4" />
                      Change URL
                    </Button>
                  </div>

                  <div className="grid gap-5 lg:grid-cols-[320px_1fr]">
                    <aside className="space-y-4">
                      <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Product snapshot
                        </p>
                        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                          {product && getProductScreenshotThumbnailUrl(product) ? (
                            <img
                              src={getProductScreenshotThumbnailUrl(product)!}
                              alt="Product screenshot"
                              className="max-h-72 w-full object-contain"
                              loading="lazy"
                              decoding="async"
                            />
                          ) : (
                            <div className="flex h-40 items-center justify-center text-sm text-gray-400">
                              No screenshot available
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="rounded-xl border border-spectrum-blue/20 bg-spectrum-blue/5 p-4">
                        <p className="text-sm font-semibold text-spectrum-blue">Before you continue</p>
                        <ul className="mt-2 space-y-2 text-sm text-gray-700">
                          <li className="flex gap-2">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-spectrum-blue" />
                            Keep product name clear and specific.
                          </li>
                          <li className="flex gap-2">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-spectrum-blue" />
                            Add 3-5 useful keywords for better reference retrieval.
                          </li>
                          <li className="flex gap-2">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-spectrum-blue" />
                            Include direct competitors for stronger inspiration.
                          </li>
                        </ul>
                      </div>
                    </aside>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="product_name">Product Name *</Label>
                        <Input
                          ref={productNameInputRef}
                          id="product_name"
                          value={editableProduct.product_name}
                          onChange={(e) => {
                            setEditableProduct((prev) => ({ ...prev, product_name: e.target.value }))
                            if (showProductNameError && e.target.value.trim()) {
                              setShowProductNameError(false)
                            }
                          }}
                          placeholder="Enter product name"
                          className={cn(showProductNameError && 'border-red-400 focus-visible:ring-red-300')}
                        />
                        {showProductNameError && (
                          <p className="text-xs text-red-600">Product name is required before continuing.</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                          id="description"
                          value={editableProduct.description}
                          onChange={(e) =>
                            setEditableProduct((prev) => ({ ...prev, description: e.target.value }))
                          }
                          placeholder="Enter product description"
                          rows={3}
                        />
                      </div>

                      <TagEditor
                        label="Search Keywords"
                        helper="Keywords used to retrieve relevant ads. Press Enter to add."
                        tags={editableProduct.keywords}
                        inputValue={newKeyword}
                        onInputChange={setNewKeyword}
                        onAdd={addKeywordTag}
                        onRemove={(idx) =>
                          setEditableProduct((prev) => ({
                            ...prev,
                            keywords: prev.keywords.filter((_, i) => i !== idx),
                          }))
                        }
                        placeholder="Add a keyword..."
                      />

                      <TagEditor
                        label="Competitor Brands"
                        helper="Well-known brands in your category for reference ad matching."
                        tags={editableProduct.competitor_brands}
                        inputValue={newBrand}
                        onInputChange={setNewBrand}
                        onAdd={addBrandTag}
                        onRemove={(idx) =>
                          setEditableProduct((prev) => ({
                            ...prev,
                            competitor_brands: prev.competitor_brands.filter((_, i) => i !== idx),
                          }))
                        }
                        placeholder="Add a competitor brand..."
                        variant="competitor"
                      />

                      <div className="space-y-2">
                        <Label htmlFor="selling_points">Selling Points</Label>
                        <Textarea
                          id="selling_points"
                          value={editableProduct.selling_points}
                          onChange={(e) =>
                            setEditableProduct((prev) => ({ ...prev, selling_points: e.target.value }))
                          }
                          placeholder="Enter selling points (one per line)"
                          rows={3}
                        />
                        <p className="text-xs text-gray-500">Separate multiple selling points with new lines.</p>
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="target_market">Target Market</Label>
                          <Input
                            id="target_market"
                            value={editableProduct.target_market}
                            onChange={(e) =>
                              setEditableProduct((prev) => ({ ...prev, target_market: e.target.value }))
                            }
                            placeholder="e.g., US, EU, Global"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="target_audience">Target Audience</Label>
                          <Input
                            id="target_audience"
                            value={editableProduct.target_audience}
                            onChange={(e) =>
                              setEditableProduct((prev) => ({ ...prev, target_audience: e.target.value }))
                            }
                            placeholder="e.g., Young professionals"
                          />
                        </div>
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="price">Price</Label>
                          <Input
                            id="price"
                            value={editableProduct.price}
                            onChange={(e) =>
                              setEditableProduct((prev) => ({ ...prev, price: e.target.value }))
                            }
                            placeholder="e.g., $29.99"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="language">Language</Label>
                          <select
                            id="language"
                            value={editableProduct.language}
                            onChange={(e) =>
                              setEditableProduct((prev) => ({ ...prev, language: e.target.value }))
                            }
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <option value="en">English</option>
                            <option value="zh">Chinese</option>
                            <option value="es">Spanish</option>
                            <option value="fr">French</option>
                            <option value="de">German</option>
                            <option value="ja">Japanese</option>
                            <option value="ko">Korean</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4">
              {(searchAdsMutation.isPending || scoreMutation.isPending) && (
                <StepLoadingStatus
                  isSearching={searchAdsMutation.isPending}
                  isScoring={scoreMutation.isPending}
                  scoringDone={scoreMutation.isSuccess}
                />
              )}

              {searchResults && !searchAdsMutation.isPending && (
                <div className="space-y-4">
                  <div className="sticky top-2 z-20 rounded-xl border border-gray-200 bg-white/95 p-4 shadow-sm backdrop-blur-sm">
                    <div className="flex flex-col gap-3">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <SelectionModeToggle
                          mode={selectionMode}
                          onModeChange={handleSelectionModeChange}
                          disabled={scoreMutation.isPending}
                        />
                        <div className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700">
                          Selected {selectedAds.length} / {ONBOARDING_MAX_SELECTION}
                        </div>
                      </div>

                      {selectionMode === 'auto' ? (
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <AdCountSelector
                            count={autoSelectCount}
                            onCountChange={handleAutoSelectCountChange}
                            max={ONBOARDING_MAX_SELECTION}
                            disabled={scoreMutation.isPending || scoredAds.length === 0}
                            label="Auto-select references"
                          />
                          <span className="text-xs text-gray-500">Sorted by score (highest first)</span>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">
                          Manual mode enabled. Click cards to select up to {ONBOARDING_MAX_SELECTION} references.
                        </p>
                      )}
                    </div>
                  </div>

                  {searchResults.product_related.length > 0 && (
                    <div className="rounded-xl border border-spectrum-blue/20 bg-spectrum-blue/5 p-3">
                      <CollapsibleAdSection
                        title="Product Related"
                        count={searchResults.product_related.length}
                        ads={searchResults.product_related}
                        expanded={expandProductRelated}
                        onToggle={() => setExpandProductRelated((prev) => !prev)}
                        selectedAdIds={selectedAds}
                        onSelectAd={handleSelectAd}
                        onPreviewAd={setPreviewAd}
                        scoredAdsMap={scoredAdsMap}
                        variant="product"
                        disabled={scoreMutation.isPending}
                      />
                    </div>
                  )}

                  {searchResults.competitor_brands.length > 0 && (
                    <div className="rounded-xl border border-spectrum-orange/25 bg-spectrum-orange/5 p-3">
                      <CollapsibleAdSection
                        title="From Competitor Brands"
                        count={searchResults.competitor_brands.length}
                        ads={searchResults.competitor_brands}
                        expanded={expandCompetitorBrands}
                        onToggle={() => setExpandCompetitorBrands((prev) => !prev)}
                        selectedAdIds={selectedAds}
                        onSelectAd={handleSelectAd}
                        onPreviewAd={setPreviewAd}
                        scoredAdsMap={scoredAdsMap}
                        variant="competitor"
                        disabled={scoreMutation.isPending}
                      />
                    </div>
                  )}

                  {searchResults.product_related.length === 0 &&
                    searchResults.competitor_brands.length === 0 && (
                      <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center">
                        <p className="text-sm text-gray-600">
                          No references found. Update product keywords and competitor brands, then try again.
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          className="mt-4"
                          onClick={() => setCurrentStep(1)}
                        >
                          Back to product details
                        </Button>
                      </div>
                    )}
                </div>
              )}
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900">Generation summary</h3>
                <p className="mt-1 text-sm text-gray-600">
                  You are ready to generate your first creative batch.
                </p>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Product</p>
                    <p className="mt-1 text-sm font-semibold text-gray-900">{product?.product_name || '-'}</p>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-gray-500">References</p>
                    <p className="mt-1 text-sm font-semibold text-gray-900">{selectedAds.length} selected</p>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Output</p>
                    <p className="mt-1 text-sm font-semibold text-gray-900">{creditsRequired} image(s)</p>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Credits required</p>
                    <p className={cn('mt-1 text-sm font-semibold', hasEnoughCredits ? 'text-gray-900' : 'text-red-600')}>
                      {creditsRequired} credits
                    </p>
                  </div>
                </div>

                <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Current balance</span>
                    <span className="font-semibold text-gray-900">{creditsAvailable} credits</span>
                  </div>
                </div>
              </div>

              {!hasEnoughCredits && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-semibold">Insufficient credits</span>
                  </div>
                  <p className="mt-2 text-sm text-red-600">
                    You need {creditsRequired - creditsAvailable} more credits.
                    <a href="/settings/subscription" className="ml-1 underline">
                      Upgrade your plan
                    </a>
                  </p>
                </div>
              )}
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-6">
              {(!taskData || taskData.status === 'pending' || taskData.status === 'processing') && (
                <div className="rounded-2xl border border-gray-200 bg-white p-5">
                  <div className="mb-4 flex items-center gap-2 text-gray-900">
                    <Loader2 className="h-5 w-5 animate-spin text-spectrum-blue" />
                    <h3 className="text-lg font-semibold">Generating your creatives</h3>
                  </div>

                  <Progress value={taskProgressRatio} className="h-2.5" />
                  <p className="mt-3 text-sm text-gray-600">
                    {taskData?.status === 'processing'
                      ? `Processing ${taskData.success_count || 0}/${taskData.requested_count || creditsRequired}...`
                      : 'Starting generation...'}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Usually around 1 minute. Great start - your first creative set is coming.
                  </p>
                </div>
              )}

              {taskData?.status === 'failed' && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-5">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-semibold">Generation failed</span>
                  </div>
                  <p className="mt-2 text-sm text-red-600">
                    {taskData.error_message || 'An unexpected error occurred during generation.'}
                  </p>
                  <Button type="button" variant="outline" className="mt-4" onClick={() => setCurrentStep(3)}>
                    Go back and retry
                  </Button>
                </div>
              )}

              {taskData?.status === 'completed' && generatedAssets.length > 0 && (
                <>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                    {generatedAssets.length} image{generatedAssets.length === 1 ? '' : 's'} generated successfully.
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {generatedAssets.map((asset: Asset & { reference_ad_id?: string }, idx) => (
                      <button
                        key={asset.id || idx}
                        type="button"
                        onClick={() => setSelectedResultIdx(idx)}
                        className="group animate-fade-in-up text-left"
                        style={{ animationDelay: `${Math.min(idx * 60, 280)}ms` }}
                      >
                        <img
                          src={asset.url || ''}
                          alt={asset.file_name || `Generated ${idx + 1}`}
                          className="aspect-square w-full rounded-xl border border-gray-200 object-cover transition-all group-hover:-translate-y-0.5 group-hover:shadow-md group-hover:ring-2 group-hover:ring-spectrum-blue/35"
                        />
                        {asset.reference_ad_id && (
                          <p className="mt-1 flex items-center justify-center gap-1 text-xs text-gray-500 transition-colors group-hover:text-spectrum-blue">
                            <LinkIcon className="h-3 w-3" />
                            View matched reference
                          </p>
                        )}
                      </button>
                    ))}
                  </div>

                  {selectedResultIdx !== null && (() => {
                    const selectedAsset = generatedAssets[selectedResultIdx] as GenerationResult
                    const refAdId = selectedAsset?.reference_ad_id
                    return (
                      <ResultDetailModal
                        result={selectedAsset}
                        referenceAd={refAdId ? referenceAdsMap[refAdId] : null}
                        currentIdx={selectedResultIdx}
                        total={generatedAssets.length}
                        onClose={() => setSelectedResultIdx(null)}
                        onPrev={() => setSelectedResultIdx(Math.max(0, selectedResultIdx - 1))}
                        onNext={() =>
                          setSelectedResultIdx(Math.min(generatedAssets.length - 1, selectedResultIdx + 1))
                        }
                      />
                    )
                  })()}
                </>
              )}

              {taskData?.status === 'completed' && generatedAssets.length === 0 && (
                <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-200 bg-gray-50 py-12">
                  <Loader2 className="h-10 w-10 animate-spin text-spectrum-blue" />
                  <p className="text-sm text-gray-600">Loading generated results...</p>
                </div>
              )}
            </div>
          )}
        </OnboardingContentFrame>

        <OnboardingActionBar
          onBack={handleBack}
          onSkip={handleSkipOnboarding}
          onNext={handleNext}
          canGoBack={currentStep > 1}
          nextLabel={nextButtonLabel}
          nextDisabled={nextButtonDisabled}
          isLoading={isActionLoading}
          loadingLabel={loadingActionLabel}
          creditsHint={creditsHint}
        />
      </div>

      {previewAd && (
        <ImagePreviewModal
          imageUrl={previewAd.url || previewAd.download_s3_url || ''}
          altText={previewAd.name || 'Ad preview'}
          onClose={() => setPreviewAd(null)}
        />
      )}
    </div>
  )
}
